import { appLogEntrySchema } from "./app-logs.js";
import { z } from "zod";
//#region src/app-fleet-logs.ts
/**
* The fleet app log search: `GET /v1/admin/apps/:appId/logs/fleet` (site-admin-api
* `AppFleetLogsController.searchFleet`, gate `cp:app:runtime:read` on the app).
*
* Plan `2026-10-21-operator-tui`, defaults row F1, ruling R-003. Producers: the query restates
* site-admin-api `appFleetLogsQuerySchema` field for field; the response is
* `AppFleetLogsService.searchFleet`, the type `AppFleetLogsResponse`, whose entries are the
* per-data-plane `AppLogEntry` lines unchanged.
*
* The search fans out over every data plane the app reports on. It has no cursor: each data plane
* mints its own and no total order exists across them. `limit` is per data plane.
*/
/**
* The query of the fleet search. Every field is optional; an unknown key is a 400.
*
* - `level`: a level word, at most 64 characters, checked against the allowlist by the service.
* - `search`: free text, at most 1024 characters.
* - `start`, `end`: ISO-8601 with an offset.
* - `limit`: 1 to 1000 lines per data plane, from a query text.
* - `fixtureId`, `versionSlug`, `container`: narrow the lines, each at most 253 characters.
*/
const appFleetLogsQuerySchema = z.object({
	level: z.string().max(64).optional(),
	search: z.string().max(1024).optional(),
	start: z.iso.datetime({ offset: true }).optional(),
	end: z.iso.datetime({ offset: true }).optional(),
	limit: z.coerce.number().int().positive().max(1e3).optional(),
	fixtureId: z.string().max(253).optional(),
	versionSlug: z.string().max(253).optional(),
	container: z.string().max(253).optional()
}).strict();
/** One data plane that answered: its lines, possibly none. */
const appFleetLogsReadItemSchema = z.object({
	dpId: z.uuid(),
	dpName: z.string().min(1),
	entries: z.array(appLogEntrySchema)
}).strict();
/**
* One data plane the CP could not search. `entries` is always empty, because nothing was read.
* `reason` is CP-written text with a correlation reference, never the upstream cause.
*/
const appFleetLogsNotCollectedItemSchema = z.object({
	dpId: z.uuid(),
	dpName: z.string().min(1),
	entries: z.array(appLogEntrySchema).length(0),
	notCollected: z.literal(true),
	reason: z.string().min(1)
}).strict();
/** One data plane of the fleet answer: searched, or not collected. */
const appFleetLogsItemSchema = z.union([appFleetLogsReadItemSchema, appFleetLogsNotCollectedItemSchema]);
/**
* The fleet answer, one item per data plane in roster order.
*
* - `appId`: the `:appId` path value.
* - `partial`: true exactly when `failedKeys` is not empty. Each failed data plane id is in
*   `failedKeys` and is also a not-collected item.
* - `perDpLimit`: the line cap of each data plane.
* - `pagingUnavailable`: the sentence that says why the answer has no cursor.
*
* An app on no data plane answers `items: []`, `partial: false`, never a 404.
*/
const appFleetLogsResponseSchema = z.object({
	appId: z.uuid(),
	items: z.array(appFleetLogsItemSchema),
	partial: z.boolean(),
	failedKeys: z.array(z.uuid()),
	perDpLimit: z.number().int().positive(),
	pagingUnavailable: z.string().min(1)
}).strict().refine((body) => body.partial === body.failedKeys.length > 0, {
	message: "partial is true exactly when failedKeys is not empty",
	path: ["partial"]
});
//#endregion
export { appFleetLogsItemSchema, appFleetLogsNotCollectedItemSchema, appFleetLogsQuerySchema, appFleetLogsReadItemSchema, appFleetLogsResponseSchema };

//# sourceMappingURL=app-fleet-logs.js.map